import * as React from 'react';
import {
  earningsPresentation,
  formatReportMoney,
  hostDisplayCopy,
  hostDisplayLocale,
  payoutCashDiffers,
  type CashAmount,
  type EarningsAmount,
  type idleSummaryFacts,
} from '@evi/shared';

/** Safely render the small translated emphasis vocabulary, never arbitrary HTML. */
export function MoneyText({ text }: { text: string }) {
  return (
    <React.Fragment>
      {text
        .split(/\*\*(.*?)\*\*/g)
        .map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part))}
    </React.Fragment>
  );
}
const small = {
  display: 'block',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: 0,
  color: 'var(--evi-text-mute)',
  overflowWrap: 'anywhere' as const,
};
function money(cents: number | null, currency: string | null, locale: string) {
  return cents == null || !currency
    ? hostDisplayCopy(locale, 'unavailable')
    : formatReportMoney(cents, currency, hostDisplayLocale(locale));
}
export function IdleSummaryLine({
  facts,
  locale = 'en',
}: {
  facts: ReturnType<typeof idleSummaryFacts>;
  locale?: string;
}) {
  if (!facts) return null;
  const complete =
    facts.billed_minutes != null && facts.rate_cents != null && facts.grace_minutes != null;
  return (
    <span data-idle-summary style={{ display: 'block', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
      <MoneyText
        text={hostDisplayCopy(locale, complete ? 'idleDetail' : 'idleUnknown', {
          amount: money(facts.amount_cents, facts.currency, locale),
          minutes: facts.billed_minutes ?? '',
          rate: money(facts.rate_cents, facts.currency, locale),
          grace: facts.grace_minutes ?? '',
        })}
      />
    </span>
  );
}
export function IdleIncluded({
  cents,
  currency,
  locale = 'en',
}: {
  cents?: number | null;
  currency?: string | null;
  locale?: string;
}) {
  return cents != null && cents > 0 ? (
    <span data-idle-included style={small}>
      <MoneyText
        text={hostDisplayCopy(locale, 'idleIncluded', {
          amount: money(cents, currency ?? null, locale),
        })}
      />
    </span>
  ) : null;
}
export function EarningsAmounts({
  items,
  current,
  locale = 'en',
  recorded = false,
}: {
  items: readonly EarningsAmount[];
  current: readonly (string | null)[];
  locale?: string;
  recorded?: boolean;
}) {
  const view = earningsPresentation(items, current);
  return (
    <span data-earnings-amounts style={{ display: 'block', minWidth: 0, overflowWrap: 'anywhere' }}>
      {view.current.length ? (
        view.current.map((i) => (
          <span data-current-currency key={i.currency} style={{ display: 'block' }}>
            {money(i.cents, i.currency, locale)}
          </span>
        ))
      ) : (
        <span style={small}>
          {view.currencies.length
            ? hostDisplayCopy(
                locale,
                'noEarnings',
                { currency: view.currencies.join(' / ') },
                recorded
              )
            : hostDisplayCopy(locale, 'unavailable')}
        </span>
      )}
      {view.historical.map((i, index) => (
        <span data-historical-currency key={i.currency ?? index} style={small}>
          <MoneyText
            text={hostDisplayCopy(
              locale,
              view.currencies.length === 1 ? 'historical' : 'otherCurrency',
              {
                currency: i.currency ?? hostDisplayCopy(locale, 'unavailable'),
                current: view.currencies[0] ?? '',
                amount: money(i.cents, i.currency, locale),
              },
              recorded
            )}
          />
        </span>
      ))}
    </span>
  );
}
export function PayoutAmount({
  cents,
  currency,
  cash,
  locale = 'en',
}: {
  cents: number | null;
  currency: string | null;
  cash?: readonly CashAmount[] | null;
  locale?: string;
}) {
  return (
    <span data-payout-amount style={{ display: 'block', overflowWrap: 'anywhere' }}>
      <strong>{money(cents, currency, locale)}</strong>
      {payoutCashDiffers({ cents, currency }, cash) &&
        cash!.map((c) => (
          <span data-cash-sent key={c.currency} style={small}>
            <MoneyText
              text={hostDisplayCopy(locale, 'sentAs', {
                amount: money(c.amount_cents, c.currency, locale),
              })}
            />
          </span>
        ))}
    </span>
  );
}
